import React from 'react';
import { Sentry } from '@/lib/sentry';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    Sentry.captureException(error, { contexts: { react: { componentStack: errorInfo.componentStack } } });
    console.error("Uncaught error:", error, errorInfo);
    this.setState({ error, errorInfo });
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="p-8 max-w-2xl mx-auto mt-10 border border-error/20 rounded-lg bg-error/5">
          <h1 className="text-2xl font-bold text-error mb-4">Something went wrong.</h1>
          <div className="bg-card p-4 rounded overflow-auto border border-error/10 mb-4">
             <pre className="text-error font-mono text-sm whitespace-pre-wrap">
               {this.state.error?.toString()}
             </pre>
          </div>
          <div className="bg-muted p-4 rounded overflow-auto border border-border">
             <pre className="text-muted-foreground font-mono text-xs whitespace-pre-wrap">
               {this.state.errorInfo?.componentStack}
             </pre>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

